import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts'
import { format, subDays } from 'date-fns'
import GetAppIcon from '@mui/icons-material/GetApp'
import { exportToPDF } from '@/utils/export'
import {
  reportService,
  VolumeReportFilters,
  VolumeReportPoint,
  downloadReportCsv,
  type StatusFunnelRow,
  type SlaReport,
  type FailureReasonRow,
  type NetDailyRow,
  type DepositSourceMixRow,
} from '@/services/reportService'
import PredictiveChart from '@/components/analytics/PredictiveChart'
import { predictiveService, ForecastRequest } from '@/services/predictiveService'
import { useState as useReactState } from 'react'
import { Tabs, Tab } from '@mui/material'
import CohortAnalysis from '@/components/analytics/CohortAnalysis'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042']

const Reports = () => {
  const [tabValue, setTabValue] = useReactState(0)
  const [forecastMetric, setForecastMetric] = useReactState<'deposits' | 'withdrawals' | 'net' | 'volume'>('deposits')
  const [forecastDays, setForecastDays] = useReactState<7 | 30 | 90>(30)
  const [forecastData, setForecastData] = useReactState<any>(null)
  const [loadingForecast, setLoadingForecast] = useReactState(false)

  const [filters, setFilters] = useState<VolumeReportFilters>({
    startDate: format(subDays(new Date(), 6), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    groupBy: 'day',
  })
  const [volumeReport, setVolumeReport] = useState<{ breakdown: VolumeReportPoint[]; totals: { deposits: number; withdrawals: number } }>({
    breakdown: [],
    totals: { deposits: 0, withdrawals: 0 },
  })
  const [channelPerformance, setChannelPerformance] = useState<
    { channel: string; total: number; successRate: number; failureRate: number; average: number; highFailure: boolean }[]
  >([])
  const [processingTimes, setProcessingTimes] = useState<{ average: number; median: number; p95: number; p99: number; perChannel: { channel: string; value: number }[] }>({
    average: 0,
    median: 0,
    p95: 0,
    p99: 0,
    perChannel: [],
  })
  type TopTransaction = {
    id: string
    type: 'deposit' | 'withdrawal'
    msisdn: string
    amount: number
    paybill: string | null
  }

  const [topTransactions, setTopTransactions] = useState<TopTransaction[]>([])
  const [topFilters, setTopFilters] = useState({ type: 'all', paybill: '', threshold: 1000 })
  const [statusFunnel, setStatusFunnel] = useState<StatusFunnelRow[]>([])
  const [slaReport, setSlaReport] = useState<SlaReport | null>(null)
  const [failureItems, setFailureItems] = useState<FailureReasonRow[]>([])
  const [netDaily, setNetDaily] = useState<NetDailyRow[]>([])
  const [sourceMix, setSourceMix] = useState<DepositSourceMixRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const volumeCsvParams = useMemo(
    () => ({
      start: filters.startDate,
      end: filters.endDate,
      group_by: filters.groupBy,
      ...(filters.paybill ? { paybill: filters.paybill } : {}),
    }),
    [filters.startDate, filters.endDate, filters.groupBy, filters.paybill],
  )
  const dateCsvParams = useMemo(
    () => ({ start: filters.startDate, end: filters.endDate }),
    [filters.startDate, filters.endDate],
  )

  const handleCsv = async (path: string, params: Record<string, string>, base: string) => {
    try {
      setError(null)
      await downloadReportCsv(path, params, base)
    } catch (e) {
      console.error(e)
      setError(e instanceof Error ? e.message : 'CSV export failed')
    }
  }

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true)
      setError(null)
      try {
        const [volumeData, channelData, processingData, topTxData, funnelRes, slaRes, failRes, netRes, mixRes] =
          await Promise.all([
            reportService.fetchVolumeReport(filters).catch((err) => {
              console.error('Error fetching volume report:', err)
              throw new Error(`Failed to fetch volume report: ${err.response?.data?.message || err.message}`)
            }),
            reportService.fetchChannelPerformance(filters).catch((err) => {
              console.error('Error fetching channel performance:', err)
              return []
            }),
            reportService.fetchProcessingTimes(filters).catch((err) => {
              console.error('Error fetching processing times:', err)
              return { average: 0, median: 0, p95: 0, p99: 0, perChannel: [] }
            }),
            reportService.fetchTopTransactions(filters).catch((err) => {
              console.error('Error fetching top transactions:', err)
              return []
            }),
            reportService.fetchStatusFunnel(filters).catch((err) => {
              console.error('Error fetching status funnel:', err)
              return { items: [] as StatusFunnelRow[] }
            }),
            reportService.fetchSlaToCompleted(filters).catch((err) => {
              console.error('Error fetching SLA:', err)
              return null
            }),
            reportService.fetchFailureReasons(filters).catch((err) => {
              console.error('Error fetching failure reasons:', err)
              return { items: [] as FailureReasonRow[] }
            }),
            reportService.fetchNetDaily(filters).catch((err) => {
              console.error('Error fetching net daily:', err)
              return { items: [] as NetDailyRow[] }
            }),
            reportService.fetchDepositSourceMix(filters).catch((err) => {
              console.error('Error fetching deposit source mix:', err)
              return { items: [] as DepositSourceMixRow[] }
            }),
          ])

        setVolumeReport(volumeData)
        setChannelPerformance(Array.isArray(channelData) ? channelData : [])
        setProcessingTimes(processingData)
        setStatusFunnel(funnelRes.items ?? [])
        setSlaReport(slaRes)
        setFailureItems(failRes.items ?? [])
        setNetDaily(netRes.items ?? [])
        setSourceMix(mixRes.items ?? [])
        
      // Normalize amount to number in case API returns it as string
        const normalized = (Array.isArray(topTxData) ? topTxData : []).map((t) => ({
        ...t,
        amount: typeof t.amount === 'string' ? parseFloat(t.amount) || 0 : Number(t.amount) || 0,
      }))
      setTopTransactions(normalized)
      } catch (err) {
        console.error('Error fetching reports:', err)
        setError(err instanceof Error ? err.message : 'Failed to load reports. Please check your connection and try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchReports()
  }, [filters])

  const generateForecast = async () => {
    setLoadingForecast(true)
    try {
      const request: ForecastRequest = {
        metric: forecastMetric,
        days: forecastDays,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }
      const data = await predictiveService.generateForecast(request)
      setForecastData(data)
    } catch (error) {
      console.error('Failed to generate forecast', error)
    } finally {
      setLoadingForecast(false)
    }
  }

  useEffect(() => {
    if (tabValue === 1) {
      generateForecast()
    }
  }, [tabValue, forecastMetric, forecastDays, filters])

  const summary = useMemo(() => {
    const depositsTotal = volumeReport.totals.deposits
    const withdrawalsTotal = volumeReport.totals.withdrawals
    const netFlow = depositsTotal - withdrawalsTotal
    const totalVolume = channelPerformance.reduce((sum, item) => sum + item.total, 0)
    const weightedSuccess = totalVolume
      ? channelPerformance.reduce((acc, item) => acc + item.successRate * item.total, 0) / totalVolume
      : 0

    return {
      depositsTotal,
      withdrawalsTotal,
      netFlow,
      successRate: weightedSuccess,
    }
  }, [volumeReport, channelPerformance])

  const filteredTopTransactions = useMemo(() => {
    return topTransactions
      .filter((tx) => (topFilters.type === 'all' ? true : tx.type === topFilters.type))
      .filter((tx) => (topFilters.paybill ? tx.paybill === topFilters.paybill : true))
      .filter((tx) => {
        const amount = typeof tx.amount === 'string' ? parseFloat(tx.amount) || 0 : Number(tx.amount) || 0
        return amount >= topFilters.threshold
      })
  }, [topTransactions, topFilters])

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Reports & Insights
      </Typography>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
          <Typography variant="body1">
            <strong>Error:</strong> {error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please check your connection and ensure the backend is running. If the problem persists, check the browser console for more details.
          </Typography>
        </Paper>
      )}

      {loading && (
        <Paper sx={{ p: 2, mb: 3, textAlign: 'center' }}>
          <Typography variant="body1">Loading reports data...</Typography>
        </Paper>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Volume Reports" />
          <Tab label="Predictive Analytics" />
          <Tab label="Cohort Analysis" />
        </Tabs>
      </Paper>

      {tabValue === 1 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Metric"
                  value={forecastMetric}
                  onChange={(e) => setForecastMetric(e.target.value as any)}
                >
                  <MenuItem value="deposits">Deposits</MenuItem>
                  <MenuItem value="withdrawals">Withdrawals</MenuItem>
                  <MenuItem value="net">Net Flow</MenuItem>
                  <MenuItem value="volume">Total Volume</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Forecast Period"
                  value={forecastDays}
                  onChange={(e) => setForecastDays(Number(e.target.value) as 7 | 30 | 90)}
                >
                  <MenuItem value={7}>7 Days</MenuItem>
                  <MenuItem value={30}>30 Days</MenuItem>
                  <MenuItem value={90}>90 Days</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button variant="contained" onClick={generateForecast} disabled={loadingForecast} fullWidth>
                  {loadingForecast ? 'Generating...' : 'Generate Forecast'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {forecastData && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <PredictiveChart
                  data={forecastData.data}
                  title={`${forecastMetric.charAt(0).toUpperCase() + forecastMetric.slice(1)} Forecast (${forecastDays} days)`}
                  metric={forecastMetric}
                  showConfidenceInterval={true}
                  forecastAccuracy={forecastData.accuracy}
                />
              </Grid>
            </Grid>
          )}
        </Box>
      )}

      {tabValue === 2 && <CohortAnalysis />}

      {tabValue === 0 && (
        <>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Deposits
            </Typography>
            <Typography variant="h5">KES {summary.depositsTotal.toFixed(2)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Total Withdrawals
            </Typography>
            <Typography variant="h5">KES {summary.withdrawalsTotal.toFixed(2)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Net Flow
            </Typography>
            <Typography variant="h5" color={summary.netFlow >= 0 ? 'success.main' : 'error.main'}>
              KES {summary.netFlow.toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Success Rate
            </Typography>
            <Typography variant="h5">{summary.successRate.toFixed(1)}%</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <TextField
              select
              label="Quick Presets"
              fullWidth
              value=""
              onChange={(e) => {
                const preset = e.target.value
                const today = new Date()
                let start: Date
                let end: Date = new Date(today)

                switch (preset) {
                  case 'today':
                    start = new Date(today)
                    end = new Date(today)
                    break
                  case 'yesterday':
                    start = new Date(today)
                    start.setDate(start.getDate() - 1)
                    end = new Date(start)
                    break
                  case 'last7':
                    start = new Date(today)
                    start.setDate(start.getDate() - 7)
                    break
                  case 'last30':
                    start = new Date(today)
                    start.setDate(start.getDate() - 30)
                    break
                  case 'last90':
                    start = new Date(today)
                    start.setDate(start.getDate() - 90)
                    break
                  case 'thisMonth':
                    start = new Date(today.getFullYear(), today.getMonth(), 1)
                    break
                  case 'lastMonth':
                    start = new Date(today.getFullYear(), today.getMonth() - 1, 1)
                    end = new Date(today.getFullYear(), today.getMonth(), 0)
                    break
                  default:
                    return
                }

                setFilters((prev) => ({
                  ...prev,
                  startDate: format(start, 'yyyy-MM-dd'),
                  endDate: format(end, 'yyyy-MM-dd'),
                }))
                e.target.value = '' // Reset select
              }}
            >
              <MenuItem value="">Select Preset...</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last7">Last 7 Days</MenuItem>
              <MenuItem value="last30">Last 30 Days</MenuItem>
              <MenuItem value="last90">Last 90 Days</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
              <MenuItem value="lastMonth">Last Month</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={filters.startDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="End Date"
              type="date"
              fullWidth
              value={filters.endDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Paybill"
              fullWidth
              value={filters.paybill || ''}
              onChange={(e) => setFilters((prev) => ({ ...prev, paybill: e.target.value || undefined }))}
              placeholder="Optional"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              label="Group By"
              fullWidth
              value={filters.groupBy}
              onChange={(e) => setFilters((prev) => ({ ...prev, groupBy: e.target.value as VolumeReportFilters['groupBy'] }))}
            >
              <MenuItem value="day">Daily</MenuItem>
              <MenuItem value="week">Weekly</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Export CSV (current date range)
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/volumes', volumeCsvParams, `volume_${filters.startDate}_${filters.endDate}`)}>
            Volume
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/channel-performance', volumeCsvParams, `channels_${filters.startDate}`)}>
            Channels
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/processing-time', volumeCsvParams, `processing_${filters.startDate}`)}>
            Processing
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/top-transactions', volumeCsvParams, `top_tx_${filters.startDate}`)}>
            Top transactions
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/status-funnel', dateCsvParams, `status_funnel_${filters.startDate}`)}>
            Status funnel
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/sla-to-completed', dateCsvParams, `sla_${filters.startDate}`)}>
            SLA
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/failure-reasons', { ...dateCsvParams, limit: '40' }, `failures_${filters.startDate}`)}>
            Failure reasons
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/net-daily', dateCsvParams, `net_daily_${filters.startDate}`)}>
            Net daily
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={() => handleCsv('/reports/deposit-source-mix', dateCsvParams, `deposit_source_${filters.startDate}`)}>
            Deposit source
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Volume Trend
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={volumeReport.breakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="deposits" stroke="#1976d2" strokeWidth={2} />
                  <Line type="monotone" dataKey="withdrawals" stroke="#dc004e" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Channel Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={channelPerformance.map((item) => ({ name: item.channel, value: item.total }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label
                  >
                    {channelPerformance.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Channel Performance
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Channel</TableCell>
                    <TableCell align="right">Total Volume</TableCell>
                    <TableCell align="right">Success %</TableCell>
                    <TableCell align="right">Failure %</TableCell>
                    <TableCell align="right">Avg Amount</TableCell>
                    <TableCell align="right">Alerts</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {channelPerformance.map((channel) => (
                    <TableRow key={channel.channel}>
                      <TableCell>{channel.channel}</TableCell>
                      <TableCell align="right">KES {channel.total.toFixed(2)}</TableCell>
                      <TableCell align="right">{channel.successRate.toFixed(1)}%</TableCell>
                      <TableCell align="right">{channel.failureRate.toFixed(1)}%</TableCell>
                      <TableCell align="right">{channel.average.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        {channel.highFailure && <Chip label="High Failure" color="error" size="small" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processing Time
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary">
                    Average (min)
                  </Typography>
                  <Typography variant="h6">{processingTimes.average.toFixed(1)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary">
                    Median
                  </Typography>
                  <Typography variant="h6">{processingTimes.median.toFixed(1)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary">
                    P95
                  </Typography>
                  <Typography variant="h6">{processingTimes.p95.toFixed(1)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary">
                    P99
                  </Typography>
                  <Typography variant="h6">{processingTimes.p99.toFixed(1)}</Typography>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ height: 240, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={processingTimes.perChannel}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="channel" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Breakdown Table</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={() => handleCsv('/reports/volumes', volumeCsvParams, `volume_breakdown_${filters.startDate}`)}
                >
                  CSV
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    exportToPDF(
                      volumeReport.breakdown,
                      `volume_report_${filters.startDate}_${filters.endDate}`,
                      'Volume Breakdown',
                      [
                        { header: 'Label', field: 'label' },
                        { header: 'Deposits', field: 'deposits' },
                        { header: 'Withdrawals', field: 'withdrawals' },
                      ]
                    )
                  }
                >
                  PDF
                </Button>
              </Stack>
            </Stack>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Label</TableCell>
                    <TableCell align="right">Deposits</TableCell>
                    <TableCell align="right">Withdrawals</TableCell>
                    <TableCell align="right">Net</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {volumeReport.breakdown.map((row) => (
                    <TableRow key={row.label}>
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">KES {row.deposits.toFixed(2)}</TableCell>
                      <TableCell align="right">KES {row.withdrawals.toFixed(2)}</TableCell>
                      <TableCell align="right">KES {(row.deposits - row.withdrawals).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Status funnel (all statuses)</Typography>
              <Button size="small" startIcon={<GetAppIcon />} variant="outlined" onClick={() => handleCsv('/reports/status-funnel', dateCsvParams, `status_funnel_${filters.startDate}`)}>
                CSV
              </Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Deposits #</TableCell>
                    <TableCell align="right">Deposits KES</TableCell>
                    <TableCell align="right">Direct #</TableCell>
                    <TableCell align="right">Direct KES</TableCell>
                    <TableCell align="right">Withdrawals #</TableCell>
                    <TableCell align="right">Withdrawals KES</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusFunnel.map((row) => (
                    <TableRow key={row.status}>
                      <TableCell>{row.status}</TableCell>
                      <TableCell align="right">{row.depositCount}</TableCell>
                      <TableCell align="right">{row.depositAmount.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.directDepositCount}</TableCell>
                      <TableCell align="right">{row.directDepositAmount.toFixed(2)}</TableCell>
                      <TableCell align="right">{row.withdrawalCount}</TableCell>
                      <TableCell align="right">{row.withdrawalAmount.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">SLA to COMPLETED (minutes)</Typography>
              <Button size="small" startIcon={<GetAppIcon />} variant="outlined" onClick={() => handleCsv('/reports/sla-to-completed', dateCsvParams, `sla_${filters.startDate}`)}>
                CSV
              </Button>
            </Stack>
            {slaReport ? (
              <Stack spacing={2}>
                <Typography variant="subtitle2">Deposits (n={slaReport.deposits.sampleSize})</Typography>
                <Typography variant="body2">
                  Median {slaReport.deposits.medianMinutes.toFixed(1)} · P95 {slaReport.deposits.p95Minutes.toFixed(1)} · P99{' '}
                  {slaReport.deposits.p99Minutes.toFixed(1)}
                </Typography>
                <Typography variant="subtitle2">Withdrawals (n={slaReport.withdrawals.sampleSize})</Typography>
                <Typography variant="body2">
                  Median {slaReport.withdrawals.medianMinutes.toFixed(1)} · P95 {slaReport.withdrawals.p95Minutes.toFixed(1)} · P99{' '}
                  {slaReport.withdrawals.p99Minutes.toFixed(1)}
                </Typography>
              </Stack>
            ) : (
              <Typography color="text.secondary">No SLA data</Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Top failure reasons (FAILED)</Typography>
              <Button size="small" startIcon={<GetAppIcon />} variant="outlined" onClick={() => handleCsv('/reports/failure-reasons', { ...dateCsvParams, limit: '40' }, `failures_${filters.startDate}`)}>
                CSV
              </Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {failureItems.slice(0, 20).map((row, i) => (
                    <TableRow key={`${row.source}-${i}`}>
                      <TableCell>{row.source}</TableCell>
                      <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.reason}</TableCell>
                      <TableCell align="right">{row.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Net daily (COMPLETED)</Typography>
              <Button size="small" startIcon={<GetAppIcon />} variant="outlined" onClick={() => handleCsv('/reports/net-daily', dateCsvParams, `net_daily_${filters.startDate}`)}>
                CSV
              </Button>
            </Stack>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={netDaily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="deposits" stroke="#1976d2" name="Deposits" dot={false} />
                  <Line type="monotone" dataKey="withdrawals" stroke="#dc004e" name="Withdrawals" dot={false} />
                  <Line type="monotone" dataKey="net" stroke="#2e7d32" name="Net" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Deposit source (COMPLETED)</Typography>
              <Button size="small" startIcon={<GetAppIcon />} variant="outlined" onClick={() => handleCsv('/reports/deposit-source-mix', dateCsvParams, `deposit_source_${filters.startDate}`)}>
                CSV
              </Button>
            </Stack>
            <Box sx={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sourceMix}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="depositTableAmount" stackId="a" fill="#1976d2" name="Deposits table" />
                  <Bar dataKey="directDepositAmount" stackId="a" fill="#00bcd4" name="Direct deposits" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
              <Typography variant="h6">Top Transactions & Outliers</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} width="100%" justifyContent="flex-end">
                <TextField
                  select
                  label="Type"
                  value={topFilters.type}
                  onChange={(e) => setTopFilters((prev) => ({ ...prev, type: e.target.value }))}
                  size="small"
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="deposit">Deposits</MenuItem>
                  <MenuItem value="withdrawal">Withdrawals</MenuItem>
                </TextField>
                <TextField
                  label="Paybill"
                  value={topFilters.paybill}
                  onChange={(e) => setTopFilters((prev) => ({ ...prev, paybill: e.target.value }))}
                  size="small"
                  placeholder="All paybills"
                />
                <TextField
                  label="Amount Threshold"
                  type="number"
                  value={topFilters.threshold}
                  onChange={(e) => setTopFilters((prev) => ({ ...prev, threshold: Number(e.target.value) || 0 }))}
                  size="small"
                />
              </Stack>
            </Stack>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {filteredTopTransactions.map((tx) => {
                const amount = typeof tx.amount === 'string' ? parseFloat(tx.amount) || 0 : Number(tx.amount) || 0
                const isOutlier = amount > topFilters.threshold * 1.5
                return (
                  <Box
                    key={tx.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      p: 2,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      border: isOutlier ? '1px solid #ff9800' : '1px solid transparent',
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle1">{tx.type}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tx.msisdn} · {tx.paybill}
                      </Typography>
                    </Box>
                    <Stack alignItems="flex-end">
                      <Typography variant="h6">KES {amount.toFixed(2)}</Typography>
                      {isOutlier && <Chip label="Outlier" color="warning" size="small" />}
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
        </>
      )}
    </Box>
  )
}

export default Reports

